import type { PlayerMovementState } from "../simulation/player-movement";
import { getSkinConfig } from "../application/skin-rules";
import { gameplayConfig } from "../config/gameplay-config";
import type { MotionProfile } from "../config/motion-config";
import {
  getPlayerBodyRippleScale,
  type PlayerBodyTrailState,
} from "../simulation/player-body-trail";
import {
  drawSnakeBody,
  drawSnakeHead,
  drawSnakePattern,
  drawSnakeTailPoint,
  getSnakeVisualDirection,
  type SnakeBodyVisualSegment,
  type SnakeFacePose,
  type SnakeVisualDrawingSurface,
} from "./snake-visual-renderer";
import { getSnakeFacePose, getSnakeMotionSegments } from "./snake-motion";

export interface PlayerDrawingSurface extends SnakeVisualDrawingSurface {
  clear(): this;
}

export const drawPlayer = (
  graphics: PlayerDrawingSurface,
  player: PlayerMovementState,
  body: PlayerBodyTrailState,
  simulationTimeMs: number,
  motionProfile: MotionProfile,
  turboActive = false,
  chompStartedAtMs: number | null = null,
): SnakeFacePose => {
  const skin = getSkinConfig(player.selectedSkinId);
  graphics.clear();
  if (turboActive) {
    const forwardX = Math.cos(player.directionRadians);
    const forwardY = Math.sin(player.directionRadians);
    const sideX = -forwardY;
    const sideY = forwardX;
    if (motionProfile.particleTravelPx > 0) {
      const streakLength =
        player.size * 2.6 + Math.sin(simulationTimeMs / 70) * player.size * 0.4;
      for (const sideOffset of [-0.7, 0, 0.7]) {
        const startX = player.position.x + sideX * player.size * sideOffset;
        const startY = player.position.y + sideY * player.size * sideOffset;
        graphics.lineStyle(sideOffset === 0 ? 6 : 4, 0x68e4ff, 0.8);
        graphics.beginPath();
        graphics.moveTo(
          startX - forwardX * player.size,
          startY - forwardY * player.size,
        );
        graphics.lineTo(
          startX - forwardX * streakLength,
          startY - forwardY * streakLength,
        );
        graphics.strokePath();
      }
    }
    graphics.lineStyle(6, 0xffd43b, 0.92);
    graphics.strokeCircle(
      player.position.x,
      player.position.y,
      player.size + 14,
    );
    graphics.lineStyle(2, 0x68e4ff, 1);
    graphics.strokeCircle(
      player.position.x,
      player.position.y,
      player.size + 19,
    );
  }
  const drawableSegments = body.segments.map((segment, index) => {
    const rippleScale = getPlayerBodyRippleScale(
      body,
      index,
      simulationTimeMs,
      motionProfile.bodyRippleScale,
      gameplayConfig.playerBody,
    );
    return {
      index,
      segment: {
        position: segment.position,
        radius:
          segment.radius *
          rippleScale *
          gameplayConfig.snakeVisual.bodyOverlapScale *
          gameplayConfig.snakeVisual.bodyWidthScale,
      } satisfies SnakeBodyVisualSegment,
    };
  });
  const head = { position: player.position, radius: player.size };
  const visualSegments = getSnakeMotionSegments(
    "player",
    head.position,
    drawableSegments.map(({ segment }) => segment),
    player.directionRadians,
    simulationTimeMs,
    motionProfile.snakeMotionScale,
    gameplayConfig.snakeMotion,
  );
  drawSnakeBody(
    graphics,
    head,
    visualSegments,
    skin.bodyColor,
    player.directionRadians,
    gameplayConfig.snakeVisual,
  );
  visualSegments.forEach((segment, index) => {
    const leader = visualSegments[index - 1]?.position ?? player.position;
    drawSnakePattern(
      graphics,
      segment.position,
      segment.radius,
      getSnakeVisualDirection(
        leader,
        segment.position,
        player.directionRadians,
      ),
      skin.bodyColor,
      skin.accentColor,
      skin.pattern,
      index + 1,
    );
  });
  const tail = visualSegments[visualSegments.length - 1];
  const tailLeader = visualSegments[visualSegments.length - 2];
  if (tail !== undefined && tailLeader !== undefined) {
    drawSnakeTailPoint(
      graphics,
      tailLeader.position,
      tail.position,
      tail.radius,
      skin.bodyColor,
      player.directionRadians,
      gameplayConfig.snakeVisual,
    );
  }
  graphics.fillStyle(0xf7fff7, 1);
  graphics.fillTriangle(
    player.position.x,
    player.position.y -
      player.size -
      gameplayConfig.playerStatus.markerGap -
      gameplayConfig.playerStatus.markerRadius * 2,
    player.position.x - gameplayConfig.playerStatus.markerRadius,
    player.position.y -
      player.size -
      gameplayConfig.playerStatus.markerGap -
      gameplayConfig.playerStatus.markerRadius * 3,
    player.position.x + gameplayConfig.playerStatus.markerRadius,
    player.position.y -
      player.size -
      gameplayConfig.playerStatus.markerGap -
      gameplayConfig.playerStatus.markerRadius * 3,
  );
  const face = getSnakeFacePose(
    "player",
    simulationTimeMs,
    chompStartedAtMs,
    motionProfile.snakeMotionScale,
    gameplayConfig.snakeMotion,
  );
  drawSnakeHead(
    graphics,
    {
      accentColor: skin.accentColor,
      bodyColor: skin.bodyColor,
      directionRadians: player.directionRadians,
      face,
      pattern: skin.pattern,
      position: player.position,
      radius: player.size,
    },
    gameplayConfig.snakeVisual,
  );
  return face;
};
